import {gql, NetworkStatus, type TypedDocumentNode} from '@apollo/client';
import {useQuery} from '@apollo/client/react';

import useProgressVariables from '../../hooks/useProgressVariables';
import type {_Object} from '../../Paramut';
import staticFallbackQuote from '../../StaticMaterials/fallbackQuote';
import type {Millisecond} from '../../Time';
import first from '../../util/first';
import type {
  GetQuotesOfTheDayQuery,
  GetQuotesOfTheDayQueryVariables,
} from './useQuoteOfTheDay.generated';

export type UseQuoteOfTheDayOptions = {
  period?: Millisecond;
  skip?: boolean;
};

export type QuoteOfTheDay<Mut extends boolean = false> = _Object<
  {
    body?: string;
  },
  Mut
>;

export default function useQuoteOfTheDay(
  now: Millisecond,
  options?: UseQuoteOfTheDayOptions,
): QuoteOfTheDay {
  const {period, skip} = options ?? {};
  const {daysReached, day, month} = useProgressVariables(now);

  const result = useQuery(GET_QUOTES_OF_THE_DAY, {
    skip,
    variables: {daysReached: daysReached ?? 0, day, month},
    pollInterval: __DEV__ ? period : 0,
  });

  const byDaysReached =
    daysReached === undefined
      ? undefined
      : first(result.data?.ByDaysReached?.docs);
  const byAnnualDate = first(result.data?.ByAnnualDate?.docs);
  const fallbackQuote = result.data?.FallbackPublication?.quote;
  const remoteQuote = byDaysReached ?? byAnnualDate ?? fallbackQuote;

  if (
    !remoteQuote &&
    result.networkStatus !== NetworkStatus.loading &&
    result.networkStatus !== NetworkStatus.setVariables
  ) {
    return {body: staticFallbackQuote.body};
  }

  return {body: remoteQuote?.body ?? undefined};
}

const GET_QUOTES_OF_THE_DAY: TypedDocumentNode<
  GetQuotesOfTheDayQuery,
  GetQuotesOfTheDayQueryVariables
> = gql`
  query GetQuotesOfTheDay($daysReached: Float!, $day: Float!, $month: Float!) {
    ByDaysReached: Quotes(
      limit: 1
      pagination: false
      where: {daysReached: {equals: $daysReached}}
    ) {
      docs {
        id
        body
      }
    }

    ByAnnualDate: Quotes(
      limit: 1
      pagination: false
      where: {
        annualDate__day: {equals: $day}
        annualDate__month: {equals: $month}
      }
    ) {
      docs {
        id
        body
      }
    }

    FallbackPublication {
      quote {
        id
        body
      }
    }
  }
`;
